/**
 * Tab pentru gestionarea Rolurilor și Permisiunilor
 * 
 * Funcționalități:
 * - Vizualizare roluri cu permisiunile lor
 * - Matrice de permisiuni (resurse × acțiuni)
 * - Editare permisiuni per rol (doar admin)
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  Divider,
  Card,
  CardContent,
  Stack,
} from '@mui/material'
import {
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as OperatorIcon,
  LocalHospital as MedicIcon,
  Engineering as TechnicianIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { 
  permissionsService, 
  Permission, 
  RoleWithPermissions,
  PermissionMatrix,
} from '../../services/permissionsApi'
import toast from 'react-hot-toast'

// Iconițe pentru roluri
const roleIcons: Record<string, React.ReactNode> = {
  admin: <AdminIcon />,
  operator: <OperatorIcon />,
  medic: <MedicIcon />,
  tehnician: <TechnicianIcon />,
}

// Culori pentru roluri
const roleColors: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  admin: 'error',
  operator: 'warning',
  medic: 'info',
  tehnician: 'success',
}

// Descrieri pentru roluri
const roleDescriptions: Record<string, string> = {
  admin: 'Acces complet la toate funcționalitățile sistemului',
  operator: 'Management clinic - utilizatori, studii, rapoarte (fără config sistem)',
  medic: 'Acces la studii asignate, rapoarte proprii, billing personal',
  tehnician: 'Acces minimal - vizualizare studii, editare pacienți',
}

// Traduceri pentru categorii
const categoryTranslations: Record<string, string> = {
  'Studii': 'Studii DICOM',
  'Rapoarte': 'Rapoarte Medicale',
  'Pacienți': 'Pacienți',
  'Documente': 'Documente',
  'Utilizatori': 'Utilizatori',
  'Instituții': 'Instituții',
  'Programări': 'Programări/Ture',
  'Billing': 'Facturare',
  'Contracte': 'Contracte',
  'Analytics': 'Statistici',
  'Nomenclator': 'Nomenclator',
  'Template-uri': 'Template-uri',
  'PACS': 'PACS',
  'Tenants': 'Tenant-uri',
  'Sistem': 'Config Sistem',
}

// Traduceri pentru acțiuni
const actionTranslations: Record<string, string> = {
  view: 'Vizualizare',
  create: 'Creare',
  edit: 'Editare',
  delete: 'Ștergere',
  assign: 'Asignare',
  merge: 'Unificare',
  finalize: 'Finalizare',
  sign: 'Semnare',
  upload: 'Încărcare',
  approve: 'Aprobare',
  export: 'Export',
  sync: 'Sincronizare',
  reset_password: 'Reset Parolă',
  assign_institutions: 'Alocare Instituții',
  all: 'Toate',
  read: 'Citire',
  write: 'Scriere',
}

interface RoleCardProps {
  role: RoleWithPermissions
  isExpanded: boolean
  onToggle: () => void
  onEdit?: () => void
  canEdit: boolean
}

const RoleCard: React.FC<RoleCardProps> = ({ role, isExpanded, onToggle, onEdit, canEdit }) => {
  return (
    <Card sx={{ mb: 1, border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main' } }}>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip icon={roleIcons[role.name] as React.ReactElement} label={role.name.toUpperCase()} color={roleColors[role.name] || 'default'} variant="filled" size="small" sx={{ height: 24, fontSize: '0.7rem', '& .MuiSvgIcon-root': { fontSize: 14 } }} />
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{roleDescriptions[role.name] || role.description}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{role.permission_codes.length} permisiuni</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {canEdit && role.name !== 'admin' && <Button size="small" variant="outlined" onClick={onEdit} startIcon={<SecurityIcon sx={{ fontSize: 14 }} />} sx={{ height: 24, fontSize: '0.7rem', textTransform: 'none' }}>Edit</Button>}
            <IconButton onClick={onToggle} size="small" sx={{ p: 0.25 }}>{isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}</IconButton>
          </Box>
        </Box>
      </CardContent>
      <Collapse in={isExpanded}>
        <Divider />
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
            {role.permission_codes.map((code) => <Chip key={code} label={code} size="small" variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />)}
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  )
}

interface PermissionMatrixTableProps {
  permissions: Permission[]
  rolePermissions: Set<string>
  onChange?: (permissionCode: string, checked: boolean) => void
  readOnly?: boolean
}

const PermissionMatrixTable: React.FC<PermissionMatrixTableProps> = ({
  permissions,
  rolePermissions,
  onChange,
  readOnly = true,
}) => {
  // Grupează permisiunile pe categorii și resurse
  const groupedPermissions = useMemo(() => {
    const groups: Record<string, Record<string, Permission[]>> = {}
    
    permissions.forEach((perm) => {
      const category = perm.category || 'Altele'
      const resource = perm.resource
      
      if (!groups[category]) {
        groups[category] = {}
      }
      if (!groups[category][resource]) {
        groups[category][resource] = []
      }
      groups[category][resource].push(perm)
    })
    
    return groups
  }, [permissions])

  // Colectează toate acțiunile unice
  const allActions = useMemo(() => {
    const actions = new Set<string>()
    permissions.forEach((p) => actions.add(p.action))
    return Array.from(actions).sort()
  }, [permissions])

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Resursă</TableCell>
            {allActions.map((action) => (
              <TableCell 
                key={action} 
                align="center"
                sx={{ fontWeight: 'bold', minWidth: 80 }}
              >
                {actionTranslations[action] || action}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(groupedPermissions).map(([category, resources]) => (
            <React.Fragment key={category}>
              {/* Header categorie */}
              <TableRow>
                <TableCell 
                  colSpan={allActions.length + 1}
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'primary.contrastText',
                    fontWeight: 'bold',
                    py: 1,
                  }}
                >
                  {categoryTranslations[category] || category}
                </TableCell>
              </TableRow>
              
              {/* Rânduri pentru fiecare resursă */}
              {Object.entries(resources).map(([resource, perms]) => (
                <TableRow key={resource} hover>
                  <TableCell sx={{ pl: 3 }}>
                    {resource.charAt(0).toUpperCase() + resource.slice(1)}
                  </TableCell>
                  {allActions.map((action) => {
                    const perm = perms.find((p) => p.action === action)
                    if (!perm) {
                      return <TableCell key={action} align="center">-</TableCell>
                    }
                    
                    const hasPermission = rolePermissions.has(perm.code)
                    
                    return (
                      <TableCell key={action} align="center">
                        {readOnly ? (
                          hasPermission ? (
                            <CheckIcon color="success" fontSize="small" />
                          ) : (
                            <CloseIcon color="disabled" fontSize="small" />
                          )
                        ) : (
                          <Checkbox
                            checked={hasPermission}
                            onChange={(e) => onChange?.(perm.code, e.target.checked)}
                            size="small"
                            disabled={perm.is_system}
                          />
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const RolesPermissionsTab: React.FC = () => {
  const { user } = useAuth()
  const [roles, setRoles] = useState<RoleWithPermissions[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRole, setExpandedRole] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<string | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [editedPermissions, setEditedPermissions] = useState<Set<string>>(new Set())
  const [isSaving, setIsSaving] = useState(false)

  const canEdit = user?.role === 'admin'

  // Încarcă datele
  const loadData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const [rolesData, permsData] = await Promise.all([
        permissionsService.listRoles(),
        permissionsService.listPermissions(),
      ])
      
      setRoles(rolesData.roles)
      setPermissions(permsData.permissions)
    } catch (err: any) {
      console.error('Eroare la încărcarea datelor:', err)
      setError(err.message || 'Eroare la încărcarea datelor')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Selectează un rol pentru editare
  const handleEditRole = (roleName: string) => {
    const role = roles.find((r) => r.name === roleName)
    if (role) {
      setSelectedRole(roleName)
      setEditedPermissions(new Set(role.permission_codes))
      setEditMode(true)
    }
  }

  // Toggle permisiune în modul editare
  const handlePermissionChange = (code: string, checked: boolean) => {
    setEditedPermissions((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(code)
      } else {
        next.delete(code)
      }
      return next
    })
  }

  // Salvează modificările
  const handleSave = async () => {
    if (!selectedRole) return
    
    const role = roles.find((r) => r.name === selectedRole)
    if (!role) return
    
    try {
      setIsSaving(true)
      
      await permissionsService.updateRolePermissions(
        role.id,
        Array.from(editedPermissions),
        true
      )
      
      toast.success(`Permisiunile pentru ${selectedRole} au fost actualizate`)
      setEditMode(false)
      setSelectedRole(null)
      await loadData()
    } catch (err: any) {
      console.error('Eroare la salvare:', err)
      toast.error(err.response?.data?.detail || 'Eroare la salvare')
    } finally {
      setIsSaving(false)
    }
  }

  // Anulează editarea
  const handleCancel = () => {
    setEditMode(false)
    setSelectedRole(null)
    setEditedPermissions(new Set())
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    )
  }

  // Modul editare
  if (editMode && selectedRole) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={roleIcons[selectedRole] as React.ReactElement}
              label={`Editare: ${selectedRole.toUpperCase()}`}
              color={roleColors[selectedRole] || 'default'}
              variant="filled"
            />
            <Typography variant="body2" color="text.secondary">
              Selectează permisiunile pentru acest rol
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Anulează
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Salvează
            </Button>
          </Box>
        </Box>
        
        <PermissionMatrixTable
          permissions={permissions}
          rolePermissions={editedPermissions}
          onChange={handlePermissionChange}
          readOnly={false}
        />
      </Box>
    )
  }

  // Vizualizare normală - Compact
  return (
    <Box sx={{ p: 1.5 }}>
      {/* Header - Compact */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SecurityIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Roluri și Permisiuni</Typography>
        </Box>
        <Tooltip title="Reîncarcă"><IconButton onClick={loadData} disabled={isLoading} size="small"><RefreshIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
      </Box>

      {/* Statistici - Compact */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        {[
          { val: roles.length, label: 'Roluri' },
          { val: permissions.length, label: 'Permisiuni' },
          { val: new Set(permissions.map(p => p.category)).size, label: 'Categorii' },
        ].map((s, i) => (
          <Paper key={i} sx={{ p: 1, flex: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'primary.main' }}>{s.val}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Lista roluri */}
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Roluri Sistem
      </Typography>
      
      {roles.map((role) => (
        <RoleCard
          key={role.id}
          role={role}
          isExpanded={expandedRole === role.name}
          onToggle={() => setExpandedRole(expandedRole === role.name ? null : role.name)}
          onEdit={() => handleEditRole(role.name)}
          canEdit={canEdit}
        />
      ))}

      {/* Matrice completă (opțional) */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Matrice Permisiuni (Toate Rolurile)
        </Typography>
        
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Permisiune</TableCell>
                {roles.map((role) => (
                  <TableCell key={role.id} align="center" sx={{ fontWeight: 'bold' }}>
                    <Chip
                      label={role.name}
                      size="small"
                      color={roleColors[role.name] || 'default'}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.slice(0, 20).map((perm) => (
                <TableRow key={perm.id} hover>
                  <TableCell>
                    <Tooltip title={perm.description || ''}>
                      <Typography variant="body2">{perm.code}</Typography>
                    </Tooltip>
                  </TableCell>
                  {roles.map((role) => (
                    <TableCell key={role.id} align="center">
                      {role.permission_codes.includes(perm.code) ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <CloseIcon color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {permissions.length > 20 && (
                <TableRow>
                  <TableCell colSpan={roles.length + 1} align="center">
                    <Typography variant="caption" color="text.secondary">
                      ... și încă {permissions.length - 20} permisiuni
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export default RolesPermissionsTab

